import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Input,
    InputNumber,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    DeleteOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi } from '../../../services/ai-admin-api';

const { Text } = Typography;

export interface PanelMember {
    provider: string;
    model: string;
    label?: string;
    enabled?: boolean;
    batchSize?: number;
    nFloor?: number;
    readCompetitors?: boolean;
    readsPages?: boolean;
}

export interface CatalogueModel {
    id: string;
    label?: string;
    supportsWebSearch?: boolean;
}

const PROVIDERS = [
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'google', label: 'Google' },
];

export function parseCatalogue(raw: string | undefined): CatalogueModel[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown): CatalogueModel | null => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as CatalogueModel;
                    if (e.id) {
                        return {
                            id: e.id,
                            label: e.label,
                            supportsWebSearch: e.supportsWebSearch,
                        };
                    }
                }
                return null;
            })
            .filter((m): m is CatalogueModel => m !== null);
    } catch {
        return [];
    }
}

export function parsePanel(raw: string | undefined): PanelMember[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as PanelMember[]) : [];
    } catch {
        return [];
    }
}

interface Props {
    value: PanelMember[];
    onChange: (next: PanelMember[]) => void;
    catalogue: Record<string, CatalogueModel[]>;
    // Fallbacks shown as placeholders, so a blank cell reads as "whatever the
    // instrument says" rather than as zero.
    batchSizeFallback?: string;
    nFloorFallback?: string;
}

// The panel is the instrument: which models were asked, and how much each one
// was asked for. Per-member batch size and n exist because tolerance for a
// crowded call and a provider's daily request ceiling are properties of the
// model, not of the study.
export const ModelBeliefsPanelEditor: React.FC<Props> = ({
    value,
    onChange,
    catalogue,
    batchSizeFallback,
    nFloorFallback,
}) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [resolved, setResolved] = useState<
        Record<string, { supportsWebSearch: boolean | null; warnings: string[] }>
    >({});

    // What a run started right now would actually use, checked against the
    // catalogue server-side. A model without web search silently measures
    // memory while the run believes it is measuring the record.
    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getModelBeliefsPanel(jwt)
            .then((panel) => {
                const next: Record<
                    string,
                    { supportsWebSearch: boolean | null; warnings: string[] }
                > = {};
                for (const member of panel.members ?? []) {
                    next[`${member.provider}:${member.model}`] = {
                        supportsWebSearch: member.supportsWebSearch,
                        warnings: member.warnings ?? [],
                    };
                }
                setResolved(next);
            })
            .catch(() => {
                // Non-fatal: the editor still works, it just cannot annotate.
            });
    }, [jwt]);

    const patch = (index: number, changes: Partial<PanelMember>) =>
        onChange(value.map((m, i) => (i === index ? { ...m, ...changes } : m)));

    const add = () =>
        onChange([
            ...value,
            {
                provider: 'anthropic',
                model: '',
                label: '',
                enabled: true,
            },
        ]);

    const modelOptions = (member: PanelMember) => {
        const list = catalogue[member.provider] ?? [];
        const options = list.map((m) => ({
            value: m.id,
            label: m.supportsWebSearch === false ? `${m.label ?? m.id} (no web search)` : m.label ?? m.id,
        }));
        // A model pinned here but missing from the catalogue must still show
        // its own value rather than rendering blank.
        if (member.model && !list.some((m) => m.id === member.model)) {
            options.unshift({ value: member.model, label: `${member.model} (not in catalogue)` });
        }
        return options;
    };

    const columns = [
        {
            title: 'On',
            key: 'enabled',
            width: 60,
            render: (_: unknown, member: PanelMember, index: number) => (
                <Switch
                    size="small"
                    checked={member.enabled !== false}
                    onChange={(checked) => patch(index, { enabled: checked })}
                />
            ),
        },
        {
            title: 'Shown as',
            key: 'label',
            width: 130,
            render: (_: unknown, member: PanelMember, index: number) => (
                <Input
                    size="small"
                    value={member.label ?? ''}
                    placeholder={member.model || 'ChatGPT'}
                    onChange={(e) => patch(index, { label: e.target.value })}
                />
            ),
        },
        {
            title: 'Provider',
            key: 'provider',
            width: 130,
            render: (_: unknown, member: PanelMember, index: number) => (
                <Select
                    size="small"
                    style={{ width: '100%' }}
                    value={member.provider}
                    options={PROVIDERS}
                    onChange={(provider: string) => patch(index, { provider })}
                />
            ),
        },
        {
            title: 'Model',
            key: 'model',
            render: (_: unknown, member: PanelMember, index: number) => {
                const state = resolved[`${member.provider}:${member.model}`];
                return (
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Select
                            size="small"
                            showSearch
                            style={{ width: '100%' }}
                            value={member.model || undefined}
                            placeholder="Pick a model"
                            options={modelOptions(member)}
                            onChange={(model: string) => patch(index, { model })}
                        />
                        {state?.supportsWebSearch === false && (
                            <Text type="danger" style={{ fontSize: 11 }}>
                                <WarningOutlined style={{ marginRight: 4 }} />
                                No web search: this member would measure memory while
                                the run reports the record.
                            </Text>
                        )}
                        {(state?.warnings ?? []).map((w) => (
                            <Text key={w} type="warning" style={{ fontSize: 11 }}>
                                {w}
                            </Text>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: (
                <Tooltip title="Statements per call for this model. Calibration: Sol and Opus hold at 12; Gemini 3.1 Pro drifts down at 12 (p=0.032), so it runs at 4.">
                    <span>Batch</span>
                </Tooltip>
            ),
            key: 'batchSize',
            width: 90,
            render: (_: unknown, member: PanelMember, index: number) => (
                <InputNumber
                    size="small"
                    style={{ width: '100%' }}
                    min={1}
                    max={20}
                    value={member.batchSize}
                    placeholder={batchSizeFallback}
                    onChange={(v) =>
                        patch(index, { batchSize: v === null ? undefined : Number(v) })
                    }
                />
            ),
        },
        {
            title: (
                <Tooltip title="Samples per statement for this model. Halving a member's n is a better answer to a daily request ceiling than dropping it from the panel.">
                    <span>n</span>
                </Tooltip>
            ),
            key: 'nFloor',
            width: 80,
            render: (_: unknown, member: PanelMember, index: number) => (
                <InputNumber
                    size="small"
                    style={{ width: '100%' }}
                    min={1}
                    max={40}
                    value={member.nFloor}
                    placeholder={nFloorFallback}
                    onChange={(v) =>
                        patch(index, { nFloor: v === null ? undefined : Number(v) })
                    }
                />
            ),
        },
        {
            title: (
                <Tooltip title="Off reads only the brand with this model. Not free: the competitor consensus is then built from fewer models than the brand consensus, and nothing may present the two as like for like.">
                    <span>Competitors</span>
                </Tooltip>
            ),
            key: 'readCompetitors',
            width: 110,
            render: (_: unknown, member: PanelMember, index: number) => (
                <Switch
                    size="small"
                    checked={member.readCompetitors !== false}
                    onChange={(checked) =>
                        patch(index, { readCompetitors: checked ? undefined : false })
                    }
                />
            ),
        },
        {
            title: (
                <Tooltip title="v4. This member reads the pages the rank question surfaced, search off, and scores each against the brand’s statements. One reader per panel: turning it on here turns it off on the others.">
                    <span>
                        Reads pages
                        <InfoCircleOutlined style={{ marginLeft: 4 }} />
                    </span>
                </Tooltip>
            ),
            key: 'readsPages',
            width: 120,
            render: (_: unknown, member: PanelMember, index: number) => (
                <Switch
                    size="small"
                    checked={member.readsPages === true}
                    onChange={(checked) =>
                        onChange(
                            value.map((m, i) => {
                                if (i === index) return { ...m, readsPages: checked ? true : undefined };
                                return checked ? { ...m, readsPages: undefined } : m;
                            }),
                        )
                    }
                />
            ),
        },
        {
            title: '',
            key: 'remove',
            width: 50,
            render: (_: unknown, __: PanelMember, index: number) => (
                <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onChange(value.filter((_m, i) => i !== index))}
                />
            ),
        },
    ];

    const enabled = value.filter((m) => m.enabled !== false && m.provider && m.model);

    return (
        <Card
            size="small"
            title="Panel"
            style={{ marginBottom: 16 }}
            extra={
                <Space>
                    <Tag color={enabled.length > 0 ? 'blue' : 'red'}>
                        {enabled.length} model{enabled.length === 1 ? '' : 's'} asked
                    </Tag>
                    <Button
                        size="small"
                        icon={<PlusOutlined style={{ marginRight: 4 }} />}
                        onClick={add}
                    >
                        Add model
                    </Button>
                </Space>
            }
        >
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="These are the models being measured, not the model that writes the read"
                description="The panel is the instrument: the three models the consumer assistants actually serve. A more capable model is the wrong instrument if no shopper is handed it. Blank batch and n fall back to the sampling defaults below."
            />
            <Table
                rowKey={(member: PanelMember) =>
                    `${member.provider}:${member.model}:${member.label ?? ''}`
                }
                size="small"
                pagination={false}
                dataSource={value}
                columns={columns as any}
            />
            {enabled.length === 0 && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="No usable panel member"
                    description="A run needs at least one enabled member with a provider and a model. With none, the planner produces no cells and the run finishes having measured nothing."
                />
            )}
        </Card>
    );
};
